import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { Toast } from '../components/Toast';
import { viewStateAtom } from '../map/atoms';
import { feedbackDialogOpenAtom } from './atoms';
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_STEPS_LENGTH,
  MAX_TITLE_LENGTH,
  type FeedbackType,
} from './constants';
import { getConsoleLog } from './consoleLogBuffer';
import { captureAppScreenshot, FEEDBACK_CAPTURE_IGNORE_CLASS } from './screenshot';
import { FeedbackSubmitError, submitFeedback } from './submitFeedback';

const DISCLAIMER_INTRO =
  'Submitting this form sends your report to the TEMPO development team along with:';

const DISCLAIMER_ITEMS = [
  'the screenshot shown above',
  "your browser's console error log for this session",
  'your TEMPO username and name',
  'the current page URL',
  'your browser version',
];

const NETWORK_ERROR_MESSAGE =
  'We could not submit your feedback. Please check your connection and try again.';

type ToastState = { id: number; severity: 'success' | 'error'; message: string };

export const FeedbackDialog = () => {
  const [open, setOpen] = useAtom(feedbackDialogOpenAtom);
  const viewState = useAtomValue(viewStateAtom);

  const [type, setType] = useState<FeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');

  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(open);
  const [captureError, setCaptureError] = useState(false);
  const [captureNonce, setCaptureNonce] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [enlargeOpen, setEnlargeOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const toastIdRef = useRef(0);

  const showToast = (severity: ToastState['severity'], message: string) => {
    setToast({ id: (toastIdRef.current += 1), severity, message });
  };

  // Opening the dialog, or retrying the capture, drops the previous screenshot
  // while rendering rather than from the effect below, which would show the
  // last capture for a frame before clearing it.
  const captureKey = `${open}:${captureNonce}`;
  const [lastCaptureKey, setLastCaptureKey] = useState(captureKey);
  if (lastCaptureKey !== captureKey) {
    setLastCaptureKey(captureKey);
    setCaptureError(false);
    setScreenshot(null);
    setCapturing(open);
  }

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    captureAppScreenshot()
      .then(dataUrl => {
        if (!cancelled) setScreenshot(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setCaptureError(true);
      })
      .finally(() => {
        if (!cancelled) setCapturing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, captureNonce]);

  const resetForm = () => {
    setType('bug');
    setTitle('');
    setDescription('');
    setSteps('');
    setScreenshot(null);
  };

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
  };

  const canSubmit =
    screenshot !== null &&
    !capturing &&
    !submitting &&
    title.trim().length > 0 &&
    description.trim().length > 0;

  const handleSubmit = async () => {
    if (screenshot === null) return;
    setSubmitting(true);
    try {
      await submitFeedback({
        type,
        title: title.trim(),
        description: description.trim(),
        stepsToReproduce: type === 'bug' && steps.trim().length > 0 ? steps.trim() : undefined,
        screenshot,
        consoleLog: getConsoleLog(),
        context: {
          pageUrl: window.location.href,
          viewport: {
            center: [viewState.longitude, viewState.latitude],
            zoom: viewState.zoom,
          },
          userAgent: navigator.userAgent,
          appVersion: __APP_VERSION__,
        },
      });
      showToast('success', 'Thank you. Your feedback has been submitted.');
      resetForm();
      setOpen(false);
    } catch (error) {
      showToast(
        'error',
        error instanceof FeedbackSubmitError ? error.message : NETWORK_ERROR_MESSAGE
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        className={FEEDBACK_CAPTURE_IGNORE_CLASS}
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ typography: 'h5', fontWeight: 600 }}>Send Feedback</DialogTitle>
        <DialogContent dividers>
          <ToggleButtonGroup
            exclusive
            color="primary"
            value={type}
            onChange={(_event: React.MouseEvent<HTMLElement>, value: FeedbackType | null) => {
              if (value) setType(value);
            }}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="bug">Bug</ToggleButton>
            <ToggleButton value="feedback">Feedback</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label="Title"
            required
            fullWidth
            margin="normal"
            value={title}
            onChange={event => setTitle(event.target.value)}
            slotProps={{ htmlInput: { maxLength: MAX_TITLE_LENGTH } }}
          />
          <TextField
            label="Description"
            required
            fullWidth
            multiline
            minRows={4}
            margin="normal"
            value={description}
            onChange={event => setDescription(event.target.value)}
            slotProps={{ htmlInput: { maxLength: MAX_DESCRIPTION_LENGTH } }}
          />
          {type === 'bug' && (
            <TextField
              label="Steps to Reproduce"
              fullWidth
              multiline
              minRows={3}
              margin="normal"
              value={steps}
              onChange={event => setSteps(event.target.value)}
              slotProps={{ htmlInput: { maxLength: MAX_STEPS_LENGTH } }}
            />
          )}

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Screenshot
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 120,
              border: theme => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1,
            }}
          >
            {capturing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Capturing screenshot...</Typography>
              </Box>
            ) : captureError ? (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  Could not capture a screenshot.
                </Typography>
                <Button size="small" onClick={() => setCaptureNonce(nonce => nonce + 1)}>
                  Retry
                </Button>
              </Box>
            ) : screenshot ? (
              <Box
                component="img"
                src={screenshot}
                alt="Screenshot of the current view"
                onClick={() => setEnlargeOpen(true)}
                sx={{ maxWidth: '100%', maxHeight: 200, cursor: 'zoom-in', display: 'block' }}
              />
            ) : null}
          </Box>

          <Alert severity="info" icon={false} sx={{ mt: 2 }}>
            <Typography variant="body2">{DISCLAIMER_INTRO}</Typography>
            <Typography component="ul" variant="body2" sx={{ my: 0.5, pl: 2.5 }}>
              {DISCLAIMER_ITEMS.map(item => (
                <li key={item}>{item}</li>
              ))}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        className={FEEDBACK_CAPTURE_IGNORE_CLASS}
        open={enlargeOpen}
        onClose={() => setEnlargeOpen(false)}
        maxWidth="lg"
      >
        {screenshot && (
          <Box
            component="img"
            src={screenshot}
            alt="Screenshot of the current view"
            sx={{ maxWidth: '100%', maxHeight: '90vh', display: 'block' }}
          />
        )}
      </Dialog>

      {toast && <Toast key={toast.id} open message={toast.message} severity={toast.severity} />}
    </>
  );
};
